import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

const steps = ["Work Order Info", "Details & Files", "Review & Submit"];

const AddWorkOrderSmart = ({
  propertyId,
  openedByUserId = null,
  uploadedBy = 1,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("work_order_draft");
    const parsedDraft = draft ? JSON.parse(draft) : {};

    return {
      description: parsedDraft.description || "",
      details: parsedDraft.details || "",
      property_id: propertyId || parsedDraft.property_id || "",
      workorder_type_id: parsedDraft.workorder_type_id || "",
      workorder_subtype_id: parsedDraft.workorder_subtype_id || "",
      location_id: parsedDraft.location_id || "",
    };
  });

  useEffect(() => {
    localStorage.setItem("work_order_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (!propertyId) return;
    setFormData((prev) => ({
      ...prev,
      property_id: propertyId,
    }));
  }, [propertyId]);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!formData.property_id && !propertyId) return;

      try {
        const res = await api.get("/workorder-types/by-property", {
          params: { property_id: formData.property_id || propertyId },
        });

        const types = res.data?.types ?? [];
        setWorkOrderTypes(types);

        if (!types.length) {
          setSnackbar({
            open: true,
            severity: "info",
            message:
              "No work order types available for this property. Try another property or contact admin.",
          });
        }
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
        setWorkOrderTypes([]);
        setSnackbar({
          open: true,
          severity: "error",
          message:
            err.response?.data?.message ||
            "Could not load work order types for this property.",
        });
      }
    };

    fetchTypes();
  }, [formData.property_id, propertyId]);

  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchSubtypesAndLocations = async () => {
      try {
        const [subtypesRes, locationsRes] = await Promise.all([
          api.get("/workorder-subtypes-by-type", {
            params: { type_id: formData.workorder_type_id },
          }),
          api.get("/workorder-locations-by-type", {
            params: { type_id: formData.workorder_type_id },
          }),
        ]);

        setSubtypes(subtypesRes.data || []);
        setLocations(locationsRes.data || []);
      } catch (err) {
        console.error("Failed to fetch subtype/location data:", err);
        setSubtypes([]);
        setLocations([]);
      }
    };

    fetchSubtypesAndLocations();

    setFormData((prev) => ({
      ...prev,
      workorder_subtype_id: "",
      location_id: "",
    }));
  }, [formData.workorder_type_id]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;

    setSelectedFiles((prev) => {
      const combined = [...prev, ...acceptedFiles];

      return combined.filter((file, index, self) => {
        return (
          index ===
          self.findIndex(
            (f) =>
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified
          )
        );
      });
    });
  }, []);

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
  });

  const selectedType = useMemo(
    () =>
      workOrderTypes.find((t) => String(t.id) === String(formData.workorder_type_id)),
    [workOrderTypes, formData.workorder_type_id]
  );

  const selectedSubtype = useMemo(
    () =>
      subtypes.find((s) => String(s.id) === String(formData.workorder_subtype_id)),
    [subtypes, formData.workorder_subtype_id]
  );

  const selectedLocation = useMemo(
    () =>
      locations.find((l) => String(l.id) === String(formData.location_id)),
    [locations, formData.location_id]
  );

  const nextStep = () => setActiveStep((prev) => prev + 1);
  const prevStep = () => setActiveStep((prev) => prev - 1);

  const validateStep = () => {
    if (activeStep === 0) {
      return !!formData.workorder_type_id;
    }

    if (activeStep === 1) {
      return (
        !!formData.description.trim() &&
        !!formData.details.trim()
      );
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const payloadJSON = {
        property_id: formData.property_id,
        description: formData.description.trim(),
        details: formData.details.trim(),
        workorder_type_id: formData.workorder_type_id,
        workorder_subtype_id: formData.workorder_subtype_id || null,
        location_id: formData.location_id || null,
        opened_by_user_id: Number.isFinite(Number(openedByUserId))
          ? Number(openedByUserId)
          : null,
      };

      const createRes = await api.post("/addWorkOrder", payloadJSON);
      const workorder_id = createRes.data?.workOrderId;

      if (!workorder_id) {
        throw new Error(
          `Missing workOrderId in response: ${JSON.stringify(createRes.data)}`
        );
      }

      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const uploadData = new FormData();
          uploadData.append("workorder_id", workorder_id);
          uploadData.append("upload_urls", file);
          uploadData.append(
            "uploaded_by",
            Number.isFinite(Number(uploadedBy)) ? Number(uploadedBy) : 1
          );

          await api.post("/workorder-files", uploadData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }

      setFormData({
        description: "",
        details: "",
        property_id: propertyId || "",
        workorder_type_id: "",
        workorder_subtype_id: "",
        location_id: "",
      });
      setSelectedFiles([]);
      setActiveStep(0);
      localStorage.removeItem("work_order_draft");

      setSnackbar({
        open: true,
        message: "Work order created successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Submit error (AddWorkOrderSmart):", err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message || err.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    maxWidth: 980,
    mx: "auto",
    borderRadius: "24px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    overflow: "hidden",
  };

  const sectionCardSx = {
    borderRadius: "20px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
    backgroundColor: "#fff",
    p: { xs: 2, sm: 2.5, md: 3 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: "14px",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: "0 0 0 3px rgba(47,99,173,0.05)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(47,99,173,0.09)",
      },
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f8fafc",
      borderRadius: "14px",
    },
  };

  const selectSx = {
    borderRadius: "14px",
    backgroundColor: "#fff",
    "& .MuiSelect-select": {
      py: 1.7,
    },
  };

  const primaryButtonSx = {
    background: "linear-gradient(135deg, #2f63ad 0%, #1f4f94 100%)",
    color: "#fff",
    fontWeight: 700,
    borderRadius: "12px",
    textTransform: "none",
    px: 3,
    minWidth: 150,
    boxShadow: "0 12px 28px rgba(47,99,173,0.22)",
    "&:hover": {
      boxShadow: "0 14px 30px rgba(47,99,173,0.28)",
      background: "linear-gradient(135deg, #2f63ad 0%, #1f4f94 100%)",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.5,
    minWidth: 110,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    backgroundColor: "#fff",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const reviewItemSx = {
    p: 1.75,
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(248,250,252,0.85)",
    height: "100%",
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card elevation={0} sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#223142", mb: 0.5 }}
            >
              Work Order Information
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Select the main category, optional subtype, and location for this work order.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Tooltip title="Select the type of work order">
                    <InputLabel sx={{ backgroundColor: "#fff", px: 0.5 }}>
                      Work Order Type
                    </InputLabel>
                  </Tooltip>
                  <Select
                    name="workorder_type_id"
                    value={formData.workorder_type_id}
                    onChange={handleFormChange}
                    required
                    sx={selectSx}
                  >
                    {workOrderTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!subtypes.length}>
                  <InputLabel sx={{ backgroundColor: "#fff", px: 0.5 }}>
                    Subtype
                  </InputLabel>
                  <Select
                    name="workorder_subtype_id"
                    value={formData.workorder_subtype_id}
                    onChange={handleFormChange}
                    sx={selectSx}
                  >
                    {subtypes.map((sub) => (
                      <MenuItem key={sub.id} value={sub.id}>
                        {sub.subtype_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!subtypes.length && !!formData.workorder_type_id && (
                  <Typography variant="caption" sx={{ color: "#8a94a6", mt: 0.8, display: "block" }}>
                    No subtype options available for this type.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!locations.length}>
                  <InputLabel sx={{ backgroundColor: "#fff", px: 0.5 }}>
                    Location
                  </InputLabel>
                  <Select
                    name="location_id"
                    value={formData.location_id}
                    onChange={handleFormChange}
                    sx={selectSx}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.location_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!locations.length && !!formData.workorder_type_id && (
                  <Typography variant="caption" sx={{ color: "#8a94a6", mt: 0.8, display: "block" }}>
                    No location options available for this type.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Property ID"
                  name="property_id"
                  value={formData.property_id}
                  disabled
                  sx={readonlyFieldSx}
                />
              </Grid>
            </Grid>
          </Card>
        );

      case 1:
        return (
          <Card elevation={0} sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#223142", mb: 0.5 }}
            >
              Details & Attachments
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Add a clear description, detailed notes, and any supporting files.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Tooltip title="Provide a short summary of the issue or request">
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={2}
                    required
                    sx={textFieldSx}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Details"
                  name="details"
                  value={formData.details}
                  onChange={handleFormChange}
                  multiline
                  rows={5}
                  required
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                  Attach Files
                </Typography>

                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: "2px dashed rgba(47,99,173,0.24)",
                    borderRadius: "18px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: isDragActive
                      ? "linear-gradient(180deg, rgba(47,99,173,0.10) 0%, rgba(47,99,173,0.05) 100%)"
                      : "linear-gradient(180deg, rgba(47,99,173,0.03) 0%, rgba(47,99,173,0.015) 100%)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 42, color: "#2f63ad", mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                    {isDragActive
                      ? "Drop files here"
                      : "Drag & drop files or click to browse"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Supported formats: images, PDF, DOC, DOCX
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {selectedFiles.length > 0 ? (
                  <Box
                    sx={{
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "16px",
                      overflow: "hidden",
                    }}
                  >
                    {selectedFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          borderBottom:
                            index !== selectedFiles.length - 1
                              ? "1px solid rgba(47,99,173,0.08)"
                              : "none",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                          <AttachFileIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "#223142",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={file.name}
                            >
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#8a94a6" }}>
                              {(file.size / 1024).toFixed(1)} KB
                            </Typography>
                          </Box>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                          sx={{
                            border: "1px solid rgba(220,38,38,0.12)",
                            borderRadius: "10px",
                            color: "#dc2626",
                            backgroundColor: "rgba(220,38,38,0.04)",
                            "&:hover": {
                              backgroundColor: "rgba(220,38,38,0.1)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#8a94a6" }}>
                    No files uploaded yet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        );

      case 2:
        return (
          <Card elevation={0} sx={sectionCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142" }}>
                Review Your Work Order
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Confirm the information below before submitting.
            </Typography>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Type
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                    {selectedType?.type || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Subtype
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                    {selectedSubtype?.subtype_name || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Location
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                    {selectedLocation?.location_name || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <HomeWorkOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Property ID
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                    {formData.property_id || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={reviewItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Description
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "#223142" }}>
                    {formData.description || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={reviewItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Details
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "#223142", whiteSpace: "pre-wrap" }}>
                    {formData.details || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={reviewItemSx}>
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                    Attached Files
                  </Typography>

                  {selectedFiles.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedFiles.map((file, idx) => (
                        <Chip
                          key={`${file.name}-${idx}`}
                          icon={<AttachFileIcon />}
                          label={file.name}
                          sx={{
                            maxWidth: 260,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ color: "#223142" }}>None</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Card sx={pageCardSx}>
      <input
        type="hidden"
        name="opened_by_user_id"
        value={openedByUserId ?? ""}
      />

      <Box
        sx={{
          px: { xs: 2, sm: 2.5, md: 3 },
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          background:
            "linear-gradient(135deg, rgba(47,99,173,0.06) 0%, rgba(47,99,173,0.015) 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                backgroundColor: "rgba(47,99,173,0.1)",
              }}
            >
              <AssignmentIcon sx={{ color: "#2f63ad" }} />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142" }}>
                Add Work Order
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Create a new work order, describe the request, and attach files if needed.
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            "& .MuiStepLabel-label": {
              fontSize: "0.9rem",
            },
            "& .MuiStepLabel-label.Mui-active": {
              fontWeight: 700,
              color: "#223142",
            },
            "& .MuiStepLabel-label.Mui-completed": {
              fontWeight: 600,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {renderStepContent()}

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: "1px solid rgba(47,99,173,0.08)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" sx={{ color: "#8a94a6" }}>
            {activeStep === 0 && "Choose the work order type, subtype, and location."}
            {activeStep === 1 && "Provide a clear description and upload any supporting files."}
            {activeStep === 2 && "Review all information before submitting the work order."}
          </Typography>

          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={prevStep}
              disabled={activeStep === 0 || isSubmitting}
              sx={secondaryButtonSx}
            >
              Back
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={nextStep}
                disabled={isSubmitting || !validateStep()}
                sx={primaryButtonSx}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={primaryButtonSx}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Submit Work Order"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AddWorkOrderSmart;